import type { Vehicle } from '~/types/strapi';

export type SchemaOrgCarOptions = {
  pageUrl: string;
  imageUrls: string[];
};

/**
 * Builds a schema.org Car + Offer JSON-LD object for vehicle detail pages.
 * Only includes properties that have values.
 * @see https://schema.org/Car
 */
export function buildSchemaOrgCar(car: Vehicle, options: SchemaOrgCarOptions): Record<string, unknown> {
  const { pageUrl, imageUrls } = options;

  const nameParts = [car.make?.name, car.model].filter(Boolean);
  const carPayload: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Car',
    ...(nameParts.length > 0 && { name: nameParts.join(' ') }),
    ...(car.description && { description: car.description }),
    ...(imageUrls.length > 0 && { image: imageUrls }),
    url: pageUrl,
    ...(car.exterior_color && { color: car.exterior_color }),
    ...(car.vin && { vehicleIdentificationNumber: car.vin }),
    ...(car.mileage != null && {
      mileageFromOdometer: {
        '@type': 'QuantitativeValue',
        value: car.mileage,
        unitCode: 'KMT',
      },
    }),
    ...(car.fuel_type && { fuelType: car.fuel_type }),
    ...(car.transmission_type && { vehicleTransmission: car.transmission_type }),
    ...(car.body_type_general && { bodyType: car.body_type_general }),
    ...(car.first_registration && { dateVehicleFirstRegistered: car.first_registration }),
    ...(car.year_of_manufacture != null && { vehicleModelDate: car.year_of_manufacture }),
    ...(car.doors != null && { numberOfDoors: car.doors }),
    ...(car.seats != null && { vehicleSeatingCapacity: car.seats }),
    ...(car.make?.name && { brand: { '@type': 'Brand', name: car.make.name } }),
  };

  if (car.power_kw != null || car.displacement_cm3 != null) {
    const parts = [];
    if (car.power_kw != null) parts.push(`${car.power_kw} kW`);
    if (car.displacement_cm3 != null) parts.push(`${car.displacement_cm3} cm³`);
    if (parts.length > 0) {
      carPayload.vehicleEngine = {
        '@type': 'EngineSpecification',
        name: parts.join(', '),
      };
    }
  }

  const availability =
    car.vehicle_status === 'Verkauft' ? 'https://schema.org/Sold' : 'https://schema.org/InStock';

  const offerPayload: Record<string, unknown> = {
    '@type': 'Offer',
    availability,
    url: pageUrl,
    ...(car.asking_price != null && {
      price: car.asking_price,
      priceCurrency: 'EUR',
    }),
  };

  carPayload.offers = offerPayload;

  console.log('carPayload', carPayload);

  return carPayload;
}
